import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import type { User, RegisterData } from '@/types/auth';
import { supabase } from '@/lib/supabaseClient';

export interface ResellerProfile {
  id: string;
  name: string;
  contact: string | null;
  district: string | null;
  type: string;
  status: string;
  wholesaleDiscountPct: number;
}

interface AuthContextType {
  currentUser: User | null;
  resellerProfile: ResellerProfile | null;
  isReseller: boolean;
  session: Session | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  needsEmailConfirmation: boolean;
  login: (email: string, password: string) => Promise<{ ok: boolean; isReseller: boolean; isStaff: boolean }>;
  register: (data: RegisterData) => Promise<boolean>;
  logout: () => Promise<void>;
  updateProfile: (data: { name?: string; phone?: string; language?: 'ko' | 'en' | 'bn' }) => Promise<boolean>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<{ ok: boolean; error?: string }>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface CustomerRow {
  id: string;
  full_name: string;
  phone: string | null;
  language: 'ko' | 'en' | 'bn';
  marketing_consent: boolean;
  member_grade: 'Bronze' | 'Silver' | 'Gold' | 'Platinum';
  created_at: string;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  area: string | null;
  postal_code: string | null;
}

async function loadUserFromSession(session: Session): Promise<User | null> {
  const { data: profile } = await supabase
    .from('rxk_customers')
    .select('id, full_name, phone, language, marketing_consent, member_grade, created_at, address_line1, address_line2, city, area, postal_code')
    .eq('id', session.user.id)
    .maybeSingle();

  if (!profile) return null;
  const p = profile as CustomerRow;

  const [{ data: mileageRows }, { count: orderCount }] = await Promise.all([
    supabase.from('rxk_mileage_ledger').select('amount').eq('customer_id', p.id),
    supabase.from('core_orders').select('id', { count: 'exact', head: true }).eq('customer_id', p.id),
  ]);

  const mileage = ((mileageRows as { amount: number }[]) ?? []).reduce((sum, r) => sum + Number(r.amount), 0);

  return {
    id: p.id,
    email: session.user.email ?? '',
    name: p.full_name,
    phone: p.phone ?? '',
    joinDate: p.created_at.slice(0, 10),
    mileage,
    totalPurchases: orderCount ?? 0,
    memberGrade: p.member_grade,
    language: p.language === 'bn' ? 'en' : p.language,
    marketingConsent: p.marketing_consent,
    savedAddress: p.address_line1
      ? {
          address1: p.address_line1,
          address2: p.address_line2 ?? '',
          city: p.city ?? 'Dhaka',
          area: p.area ?? '',
          postalCode: p.postal_code ?? '',
        }
      : null,
  };
}

async function loadResellerFromSession(session: Session): Promise<ResellerProfile | null> {
  const { data } = await supabase
    .from('core_partners')
    .select('id, name, contact, district, type, status, wholesale_discount_pct')
    .eq('user_id', session.user.id)
    .maybeSingle();
  if (!data) return null;
  return {
    id: data.id,
    name: data.name,
    contact: data.contact,
    district: data.district,
    type: data.type,
    status: data.status,
    wholesaleDiscountPct: Number(data.wholesale_discount_pct) || 0,
  };
}

// A reseller is invited without the rxk_customer metadata flag, so they
// never get an rxk_customers row — loadUserFromSession alone would leave
// them looking logged-out. Resolve both in one place so every call site
// (initial load, auth-state listener, login) treats a linked core_partners
// row as an equally valid signed-in identity, just a different one.
async function resolveSession(session: Session): Promise<{ user: User | null; reseller: ResellerProfile | null }> {
  const user = await loadUserFromSession(session);
  if (user) return { user, reseller: null };
  const reseller = await loadResellerFromSession(session);
  return { user: null, reseller };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [resellerProfile, setResellerProfile] = useState<ResellerProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [needsEmailConfirmation, setNeedsEmailConfirmation] = useState(false);

  const refreshUser = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    if (data.session) {
      const { user, reseller } = await resolveSession(data.session);
      setCurrentUser(user);
      setResellerProfile(reseller);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(async ({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      if (data.session) {
        const { user, reseller } = await resolveSession(data.session);
        if (mounted) {
          setCurrentUser(user);
          setResellerProfile(reseller);
        }
      }
      if (mounted) setIsLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setTimeout(async () => {
        if (!mounted) return;
        setSession(nextSession);
        if (nextSession) {
          const { user, reseller } = await resolveSession(nextSession);
          if (mounted) {
            setCurrentUser(user);
            setResellerProfile(reseller);
          }
        } else {
          setCurrentUser(null);
          setResellerProfile(null);
        }
      }, 0);
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error || !data.session) return { ok: false, isReseller: false, isStaff: false };
    // Populate currentUser synchronously with the resolved login, rather
    // than waiting for the onAuthStateChange listener's deferred fetch —
    // the login page navigates immediately on success, and ProtectedRoute
    // reads isAuthenticated with no loading grace period, so a
    // still-unresolved profile at that instant would bounce back to
    // /login before the async profile fetch ever finished. Returning
    // isReseller directly (rather than making the caller re-read context
    // right after) sidesteps the state-update-is-async gap.
    const { user, reseller } = await resolveSession(data.session);
    setSession(data.session);
    setCurrentUser(user);
    setResellerProfile(reseller);
    // A staff account (user_roles row) has neither an rxk_customers nor a
    // core_partners row, so it resolves to { user: null, reseller: null }
    // above — isAuthenticated (customer-or-reseller only) is deliberately
    // false for it. The login page needs to know to send it to /admin
    // instead of /my, where ProtectedRoute would otherwise immediately
    // bounce it back to /login for "not being authenticated".
    const { data: roleRow } = await supabase.from('user_roles').select('role').eq('user_id', data.session.user.id).maybeSingle();
    return { ok: true, isReseller: !!reseller, isStaff: !!roleRow };
  }, []);

  const register = useCallback(async (data: RegisterData) => {
    setNeedsEmailConfirmation(false);
    const { data: signUpData, error } = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
      options: {
        data: {
          rxk_customer: true,
          full_name: data.name,
          phone: data.phone,
          marketing_consent: data.marketingConsent,
        },
      },
    });
    if (error) return false;
    // The rxk_customers profile row is created server-side by a DB trigger
    // on auth.users insert (works even before email confirmation completes).
    if (!signUpData.session) {
      setNeedsEmailConfirmation(true);
    } else {
      // Email confirmation is off for this project (or already satisfied) —
      // a session came back immediately, so populate currentUser the same
      // way login() does rather than waiting on the deferred auth listener.
      const user = await loadUserFromSession(signUpData.session);
      setSession(signUpData.session);
      setCurrentUser(user);
    }
    return true;
  }, []);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setCurrentUser(null);
    setResellerProfile(null);
    setSession(null);
  }, []);

  const updateProfile = useCallback(
    async (data: { name?: string; phone?: string; language?: 'ko' | 'en' | 'bn' }) => {
      if (!session) return false;
      const patch: Record<string, unknown> = {};
      if (data.name !== undefined) patch.full_name = data.name;
      if (data.phone !== undefined) patch.phone = data.phone;
      if (data.language !== undefined) patch.language = data.language;

      const { error } = await supabase.from('rxk_customers').update(patch).eq('id', session.user.id);
      if (error) return false;
      await refreshUser();
      return true;
    },
    [session, refreshUser],
  );

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      if (!session?.user.email) return { ok: false, error: 'Not signed in' };
      const { error: verifyError } = await supabase.auth.signInWithPassword({
        email: session.user.email,
        password: currentPassword,
      });
      if (verifyError) return { ok: false, error: 'Current password is incorrect' };
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) return { ok: false, error: error.message };
      return { ok: true };
    },
    [session],
  );

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        resellerProfile,
        isReseller: !!resellerProfile,
        session,
        isAuthenticated: !!currentUser || !!resellerProfile,
        isLoading,
        needsEmailConfirmation,
        login,
        register,
        logout,
        updateProfile,
        changePassword,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used within AuthProvider');
  return ctx;
}
